/**
 * Shared utility functions for document periodicity calculations.
 *
 * renewal_type enum: "none" | "monthly" | "yearly" | "fixed_date" | "custom_interval"
 * renewal_interval examples: "2 mons", "4 months", "3 mons", "6 mons", {months: 2}
 *
 * All public functions use 1-based months (1=January, 12=December).
 */

/**
 * Parses a date string as local time (avoids UTC timezone shift).
 * "2024-06-01" → June 1 local, not May 31 in negative UTC offsets.
 */
function parseLocalDate(input: string | Date): Date {
  if (input instanceof Date) return input;
  // If it's a YYYY-MM-DD string (no time component), parse as local
  if (typeof input === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(input)) {
    const [y, m, d] = input.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  return new Date(input);
}

const MONTH_ABBREV_MAP: Record<string, number> = {
  JAN: 1, FEB: 2, MAR: 3, APR: 4, MAY: 5, JUN: 6,
  JUL: 7, AUG: 8, SEP: 9, OCT: 10, NOV: 11, DEC: 12,
};

/**
 * Converts a month abbreviation ("JAN", "FEB", etc.) to a 1-based month number.
 * Case insensitive. Returns 1 as fallback for invalid input.
 */
export function monthAbbrevToNumber(abbrev: string): number {
  if (!abbrev) return 1;
  return MONTH_ABBREV_MAP[abbrev.toUpperCase()] ?? 1;
}

/**
 * Parses renewal_interval into the number of months.
 * Handles: "2 mons", "4 months", "12 mons", {months: 6}, null/undefined.
 * Returns 1 as fallback (treated as monthly).
 */
export function parseIntervalMonths(
  renewalInterval: string | { months: number } | null | undefined
): number {
  if (renewalInterval == null) return 1;

  if (typeof renewalInterval === 'object' && 'months' in renewalInterval) {
    return renewalInterval.months || 1;
  }

  const str = String(renewalInterval);
  const match = str.match(/(\d+)\s*mon/i);
  if (match) {
    return parseInt(match[1], 10) || 1;
  }

  // Try extracting any number
  const numMatch = str.match(/(\d+)/);
  if (numMatch) {
    return parseInt(numMatch[1], 10) || 1;
  }

  return 1;
}

/**
 * Given a 1-based month and an interval in months, returns the 1-based start month
 * of the period that contains the given month.
 *
 * Example: month=4, intervalMonths=4 → period start = 1 (Jan-Apr)
 * Example: month=5, intervalMonths=4 → period start = 5 (May-Aug)
 * Example: month=2, intervalMonths=2 → period start = 1 (Jan-Feb)
 */
export function getPeriodStartMonth(month: number, intervalMonths: number): number {
  if (intervalMonths <= 0 || intervalMonths > 12) return month;
  // Convert to 0-based, floor-divide, convert back to 1-based
  return Math.floor((month - 1) / intervalMonths) * intervalMonths + 1;
}

export interface TargetPeriodParams {
  dueDate?: string | Date | null;
  renewalType: string | null | undefined;
  renewalInterval?: string | { months: number } | null | undefined;
  applicableMonth?: string | null; // "JAN", "FEB", etc.
  applicableYear?: number | null;
  uploadDate?: string | Date | null;
}

/**
 * Calculates the target period (month/year) that a document belongs to.
 * Returns 1-based month.
 *
 * Priority:
 * 1. applicableMonth + applicableYear (if both present)
 * 2. dueDate + renewalType/renewalInterval logic
 * 3. uploadDate (fallback)
 * 4. Current date (last resort)
 */
export function getTargetPeriod(params: TargetPeriodParams): { month: number; year: number } {
  const {
    dueDate,
    renewalType,
    renewalInterval,
    applicableMonth,
    applicableYear,
    uploadDate,
  } = params;

  // Priority 1: explicit applicable month/year
  if (applicableMonth && applicableYear) {
    return {
      month: monthAbbrevToNumber(applicableMonth),
      year: applicableYear,
    };
  }

  // Priority 2: due_date + renewal logic
  if (dueDate) {
    const date = parseLocalDate(dueDate);
    if (!isNaN(date.getTime())) {
      const dueMonth = date.getMonth() + 1; // 1-based
      const dueYear = date.getFullYear();

      switch (renewalType) {
        case 'monthly':
          return { month: dueMonth, year: dueYear };

        case 'yearly':
          return { month: 1, year: dueYear };

        case 'custom_interval': {
          const intervalMonths = parseIntervalMonths(renewalInterval);
          const startMonth = getPeriodStartMonth(dueMonth, intervalMonths);
          return { month: startMonth, year: dueYear };
        }

        case 'fixed_date':
          return { month: dueMonth, year: dueYear };

        default:
          return { month: dueMonth, year: dueYear };
      }
    }
  }

  // Priority 3: upload date fallback
  if (uploadDate) {
    const date = parseLocalDate(uploadDate);
    if (!isNaN(date.getTime())) {
      return {
        month: date.getMonth() + 1,
        year: date.getFullYear(),
      };
    }
  }

  // Last resort: current date
  const now = new Date();
  return { month: now.getMonth() + 1, year: now.getFullYear() };
}

/**
 * Returns which 1-based months of the year require a document for a given renewal type.
 *
 * monthly → [1,2,3,...,12]
 * yearly → [1]
 * custom_interval 2 mons → [1,3,5,7,9,11]
 * custom_interval 3 mons → [1,4,7,10]
 * custom_interval 4 mons → [1,5,9]
 * custom_interval 6 mons → [1,7]
 * none → []
 */
export function getExpectedMonthsForYear(
  renewalType: string | null | undefined,
  renewalInterval?: string | { months: number } | null | undefined
): number[] {
  switch (renewalType) {
    case 'monthly':
      return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

    case 'yearly':
      return [1];

    case 'custom_interval': {
      const intervalMonths = parseIntervalMonths(renewalInterval);
      const months: number[] = [];
      for (let m = 1; m <= 12; m += intervalMonths) {
        months.push(m);
      }
      return months;
    }

    case 'fixed_date':
      return [1]; // One document per year at fixed date

    case 'none':
    case null:
    case undefined:
      return [];

    default:
      return [];
  }
}

/**
 * Returns the number of documents expected per year for a given renewal type.
 */
export function getExpectedDocumentsPerYear(
  renewalType: string | null | undefined,
  renewalInterval?: string | { months: number } | null | undefined
): number {
  return getExpectedMonthsForYear(renewalType, renewalInterval).length;
}

export interface PeriodDateRange {
  startDate: Date;
  endDate: Date;
}

/**
 * Returns the date range for a period.
 *
 * @param renewalType - The renewal type
 * @param renewalInterval - The renewal interval
 * @param year - The year
 * @param month - 1-based month (period start month)
 */
export function getPeriodDateRange(
  renewalType: string | null | undefined,
  renewalInterval: string | { months: number } | null | undefined,
  year: number,
  month: number
): PeriodDateRange {
  switch (renewalType) {
    case 'monthly': {
      const startDate = new Date(year, month - 1, 1);
      const endDate = new Date(year, month, 0); // Last day of month
      return { startDate, endDate };
    }

    case 'yearly': {
      const startDate = new Date(year, 0, 1);
      const endDate = new Date(year, 11, 31);
      return { startDate, endDate };
    }

    case 'custom_interval': {
      const intervalMonths = parseIntervalMonths(renewalInterval);
      const startDate = new Date(year, month - 1, 1);
      const endDate = new Date(year, month - 1 + intervalMonths, 0); // Last day of last month in period
      return { startDate, endDate };
    }

    case 'fixed_date': {
      const startDate = new Date(year, 0, 1);
      const endDate = new Date(year, 11, 31);
      return { startDate, endDate };
    }

    default: {
      // Treat as monthly
      const startDate = new Date(year, month - 1, 1);
      const endDate = new Date(year, month, 0);
      return { startDate, endDate };
    }
  }
}

/**
 * Formats a target period as "YYYY-MM" string.
 */
export function formatPeriod(month: number, year: number): string {
  return `${year}-${month.toString().padStart(2, '0')}`;
}
